import Checkbox from '@/components/forms/Checkbox';
import InputField from '@/components/forms/InputField';
import SelectField from '@/components/forms/SelectField';
import TermsAndPrivacyNotice from '@/components/forms/TermsAndPrivacyNotice';
import RequestDemoFormElement from '@/components/RequestDemo/RequestDemoFormElement';
import { GTM_EVENTS, GTM_VARIABLES } from '@/constants/gtm';
import {
  HubspotCompanySizeDropdownOptions,
  HubspotFieldLabels,
  HubspotFieldNames,
  HubspotFieldValidators,
  HubspotFormIDs
} from '@/constants/hubspot';
import { OPTIMIZELY_EVENTS } from '@/constants/optimizely';
import { OneTrustContext } from '@/context/onetrust';
import { UserContext } from '@/context/user';
import { useCustomFormik, useFormikHubspotSubmit } from '@/lib/formik';
import gtm from '@/lib/gtm';
import optimizely from '@/lib/optimizely';
import { isWorkEmail } from '@/lib/validation';
import { FC, useContext, useEffect, useState } from 'react';
import { PrimaryInnerGlowButton } from '../buttons/primary';

export type DemoFormStepOneFields = {
  [HubspotFieldNames.CompanySize]: string;
  [HubspotFieldNames.Email]: string;
  [HubspotFieldNames.MarketingEmailOptOut]: boolean;
}

export type DemoFormStepOneProps = {
  onDemoFormOneSuccess: (values: DemoFormStepOneFields) => void;
}

const DemoFormStepOne: FC<DemoFormStepOneProps> = ({ onDemoFormOneSuccess }) => {
  const { getTempUserData } = useContext(UserContext);
  const { isGDPR } = useContext(OneTrustContext);
  // Marketing email opt in checkbox display value - we send the opposite of this value to as MarketingEmailOptOut to the backend
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(isGDPR ? false : true);


  // Sync marketingEmailOptIn state when isGDPR changes (fixes race condition with OneTrust initialization)
  useEffect(() => {
    setMarketingEmailOptIn(isGDPR ? false : true);
    formik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, isGDPR ? true : false);
  }, [isGDPR]);

  const formik = useCustomFormik<DemoFormStepOneFields>({
    initialValues: {
      [HubspotFieldNames.CompanySize]: '',
      [HubspotFieldNames.Email]: '',
      // Marketing email opt in - the user must now actively opt in, so in GDPR regions the initial value is opted out. 
      [HubspotFieldNames.MarketingEmailOptOut]: isGDPR ? true : false,
    },
    onSubmit: useFormikHubspotSubmit({
      formId: HubspotFormIDs.RequestDemoStarted,
      name: 'Request Demo Modal Start',
      content: 'Request Demo Modal Start',
      onSuccess: async (values) => {
        gtm.event(GTM_EVENTS.DEMO_STARTED, {
          [GTM_VARIABLES.USER_EMAIL]: values[HubspotFieldNames.Email],
          [GTM_VARIABLES.COMPANY_SIZE]: values[HubspotFieldNames.CompanySize],
        });

        optimizely.event(OPTIMIZELY_EVENTS.DEMO_STARTED);
        onDemoFormOneSuccess(values);
      },
      onError: (error) => {
        //
      },
    }),
    validate: (values) => {
      const errors = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        {
          required: true,
          allowPersonalEmail: true,
          allowSharedEmail: true,
        },
      );

      errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
        values[HubspotFieldNames.CompanySize],
        {
          required: true,
        },
      );

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
    validateOnBlur: false,
  });

  useEffect(() => {
    const data = getTempUserData();

    Object.keys(data).forEach((key) => {
      formik.setFieldValue(key, data[key]);
    });
  }, []);

  const isNotWorkEmail = !isWorkEmail(formik.values[HubspotFieldNames.Email]);

  return (
    <RequestDemoFormElement onSubmit={formik.handleSubmit} noValidate>
      <InputField
        type="email"
        name={HubspotFieldNames.Email}
        label={HubspotFieldLabels.Email}
        placeholder="you@company.com"
        value={formik.values[HubspotFieldNames.Email]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        invalid={formik.touched[HubspotFieldNames.Email] && !!formik.errors[HubspotFieldNames.Email]}
        warningMessage={isNotWorkEmail ? "Please enter your business email." : undefined}
        errorMessage={formik.errors[HubspotFieldNames.Email] as string}
        warningMessageColorRed={true}
      />

      <SelectField
        name={HubspotFieldNames.CompanySize}
        label={HubspotFieldLabels.CompanySize}
        options={HubspotCompanySizeDropdownOptions}
        value={formik.values[HubspotFieldNames.CompanySize]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        invalid={formik.touched[HubspotFieldNames.CompanySize] && !!formik.errors[HubspotFieldNames.CompanySize]}
        errorMessage={formik.errors[HubspotFieldNames.CompanySize] as string}
        translucent
      />


      {isGDPR && (
        <div className="flex flex-row mb-1">
          <Checkbox
            id={HubspotFieldNames.MarketingEmailOptOut}
            checked={marketingEmailOptIn}
            className="my-auto [&_input]:border-gray-600"
            onToggle={(checked) => {
              // Since we're using the same field for both the signup and demo modal, we need to invert the value since we're opting IN rather than opting out
              setMarketingEmailOptIn(checked);
              formik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, !checked);
            }}
          />
          <label className="ml-1 text-micro2 text-balance" htmlFor={HubspotFieldNames.MarketingEmailOptOut}>
            Opt into emails with the latest news, offers, promotions, and resources from Front.
          </label>
        </div>
      )}
      <TermsAndPrivacyNotice />
      <div className="flex justify-end">
        <PrimaryInnerGlowButton type="submit" disabled={formik.isValidating || formik.isSubmitting} >
          {formik.isValidating || formik.isSubmitting ? 'Submitting' : 'Continue'}
        </PrimaryInnerGlowButton>
      </div>
    </RequestDemoFormElement>
  );
};

export default DemoFormStepOne;
